import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { DialogModal, Dropdown, Pagination } from '@/components';
import { AllGroupsCard, CreateGroupModal, GroupCard, PlannedLessonModal } from '../components';
import { getPlannedLessonBlocker, PLANNED_LESSON_BLOCKER, PLANNED_LESSON_DIALOG_CONFIRM } from '../components/planned-lesson-modal';
import { useExpertGroups, useExpertStudents } from '../hooks';
import { resolveGroupGradeId, toStudentPickerItem } from '../boutique-classroom.mappers';
import {
	EMPTY_VALUE_LABEL,
	EXPERT_STUDENTS_PAGE_SIZE,
	EXPERT_STUDENTS_PAGE_SIZE_OPTIONS,
	EXPERT_STUDENTS_PICKER_PAGE_SIZE,
	PAGE_SIZE_LABEL,
	PAGINATION_FIRST_PAGE,
	TABLE_SCROLL_DURATION,
	TABLE_SCROLL_OFFSET
} from '../boutique-classroom.constants';
import { animateScrollTo, findScrollContainer } from '@/utility/animate-scroll';
import { ALL_GROUPS_ID, CreateGroupFormValues } from '../boutique-classroom.types';
import { GroupDetailDrawer, StudentsTable } from './components';
import { BoutiqueStudent, FilterOption, PendingGroupChange, PendingMemberRemoval, SortDirection, StudentSortKeyValue } from './boutique-students.types';
import {
	ALL_GRADES_FILTER_ID,
	ALL_GRADES_FILTER_LABEL,
	ALL_GROUPS_FILTER_ID,
	ALL_GROUPS_FILTER_LABEL,
	DEFAULT_SORT_DIRECTION,
	GRADE_FILTER_PLACEHOLDER,
	GROUP_CHANGE_DIALOG,
	REMOVE_MEMBER_DIALOG,
	RemoveMemberKind
} from './boutique-students.constants';
import '@/assets/styles/views/boutique-classroom/boutique-students.styles.scss';

const ALL_GROUPS_OPTION: FilterOption<number> = { id: ALL_GROUPS_FILTER_ID, name: ALL_GROUPS_FILTER_LABEL };
const ALL_GRADES_OPTION: FilterOption<string> = { id: ALL_GRADES_FILTER_ID, name: ALL_GRADES_FILTER_LABEL };
const LOADING_TEXT = 'Yükleniyor...';

interface PlannedLessonBlocker {
	id: number;
	name: string;
	reason: string;
}

const PAGE_SIZE_OPTIONS: FilterOption<number>[] = EXPERT_STUDENTS_PAGE_SIZE_OPTIONS.map(size => ({ id: size, name: String(size) }));

const BoutiqueStudentsScreen: React.FC = () => {
	const [currentPage, setCurrentPage] = useState<number>(PAGINATION_FIRST_PAGE);
	const [pageSize, setPageSize] = useState<number>(EXPERT_STUDENTS_PAGE_SIZE);
	const { students, totalPages, totalElements, isLoading: isStudentsLoading, isFetching: isStudentsFetching } = useExpertStudents({ page: currentPage - PAGINATION_FIRST_PAGE, size: pageSize });
	const { students: pickerStudents } = useExpertStudents({ size: EXPERT_STUDENTS_PICKER_PAGE_SIZE });
	const [knownStudents, setKnownStudents] = useState<Record<number, BoutiqueStudent>>({});
	const tableRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		const incoming = [...pickerStudents, ...students];
		if (!incoming.length) return;
		setKnownStudents(prev => incoming.reduce((acc, student) => ({ ...acc, [student.id]: student }), prev));
	}, [pickerStudents, students]);

	const { groups, isLoading: isGroupsLoading, isMutating, createGroupFromStudents, renameGroup, moveStudentsToGroup, removeStudentFromGroup, removeGroupMember } = useExpertGroups();

	const [selectedGroupCard, setSelectedGroupCard] = useState<number>(ALL_GROUPS_ID);
	const [drawerGroupId, setDrawerGroupId] = useState<number | null>(null);
	const [groupFilter, setGroupFilter] = useState<FilterOption<number>>(ALL_GROUPS_OPTION);
	const [gradeFilter, setGradeFilter] = useState<FilterOption<string> | null>(null);
	const [sortKey, setSortKey] = useState<StudentSortKeyValue | null>(null);
	const [sortDirection, setSortDirection] = useState<SortDirection>(DEFAULT_SORT_DIRECTION);
	const [selectedIds, setSelectedIds] = useState<number[]>([]);
	const [isGroupModalOpen, setIsGroupModalOpen] = useState(false);

	const drawerGroup = useMemo(() => groups.find(group => group.id === drawerGroupId) ?? null, [drawerGroupId, groups]);
	const drawerStudents = useMemo<BoutiqueStudent[]>(() => {
		if (!drawerGroup) return [];
		return drawerGroup.students.map(
			member =>
				knownStudents[member.id] ?? {
					id: member.id,
					fullName: member.fullName,
					gradeLabel: EMPTY_VALUE_LABEL,
					groupId: drawerGroup.id,
					groupName: drawerGroup.name,
					schoolName: EMPTY_VALUE_LABEL,
					targetUniversity: EMPTY_VALUE_LABEL,
					targetDepartment: EMPTY_VALUE_LABEL
				}
		);
	}, [drawerGroup, knownStudents]);

	const groupOptions = useMemo<FilterOption<number>[]>(() => groups.map(group => ({ id: group.id, name: group.name })), [groups]);
	const groupFilterOptions = useMemo(() => [ALL_GROUPS_OPTION, ...groupOptions], [groupOptions]);
	const gradeOptions = useMemo<FilterOption<string>[]>(() => {
		const labels = Array.from(new Set(students.map(student => student.gradeLabel))).sort((a, b) => a.localeCompare(b, 'tr'));
		return [ALL_GRADES_OPTION, ...labels.map(label => ({ id: label, name: label }))];
	}, [students]);

	const handleCloseDrawer = useCallback(() => {
		setDrawerGroupId(null);
		setSelectedGroupCard(ALL_GROUPS_ID);
	}, []);

	const handleGroupCardSelect = useCallback((groupId: number) => {
		setSelectedGroupCard(groupId);
		setDrawerGroupId(groupId === ALL_GROUPS_ID ? null : groupId);
	}, []);

	const handleGroupFilterSelect = useCallback((option: FilterOption<number>) => {
		setGroupFilter(option);
		setCurrentPage(PAGINATION_FIRST_PAGE);
	}, []);

	const handleGradeFilterSelect = useCallback((option: FilterOption<string>) => {
		setGradeFilter(option);
		setCurrentPage(PAGINATION_FIRST_PAGE);
	}, []);

	const pendingScrollRef = useRef(false);
	const fetchStartedRef = useRef(false);
	const cancelScrollAnimationRef = useRef<(() => void) | null>(null);
	const scrollToTable = useCallback(() => {
		pendingScrollRef.current = true;
	}, []);

	useEffect(() => {
		if (isStudentsFetching) {
			if (pendingScrollRef.current) fetchStartedRef.current = true;
			return;
		}
		if (!pendingScrollRef.current || !fetchStartedRef.current) return;
		pendingScrollRef.current = false;
		fetchStartedRef.current = false;
		const element = tableRef.current;
		if (!element) return;
		const scroller = findScrollContainer(element);
		const elementTop = element.getBoundingClientRect().top;
		const scrollerTop = scroller instanceof Window ? 0 : scroller.getBoundingClientRect().top;
		const current = scroller instanceof Window ? scroller.scrollY : scroller.scrollTop;
		cancelScrollAnimationRef.current?.();
		cancelScrollAnimationRef.current = animateScrollTo(scroller, current + elementTop - scrollerTop - TABLE_SCROLL_OFFSET, TABLE_SCROLL_DURATION);
	}, [isStudentsFetching, students]);

	useEffect(() => () => cancelScrollAnimationRef.current?.(), []);

	const handlePageChange = useCallback(
		(page: number) => {
			setCurrentPage(page);
			scrollToTable();
		},
		[scrollToTable]
	);

	const pageSizeOption = useMemo(() => PAGE_SIZE_OPTIONS.find(option => option.id === pageSize) ?? PAGE_SIZE_OPTIONS[0], [pageSize]);
	const handlePageSizeChange = useCallback(
		(option: FilterOption<number>) => {
			setPageSize(option.id);
			setCurrentPage(PAGINATION_FIRST_PAGE);
			scrollToTable();
		},
		[scrollToTable]
	);

	const visibleStudents = useMemo(() => {
		const filtered = students.filter(student => {
			if (groupFilter.id !== ALL_GROUPS_FILTER_ID && student.groupId !== groupFilter.id) return false;
			return !gradeFilter || gradeFilter.id === ALL_GRADES_FILTER_ID || student.gradeLabel === gradeFilter.id;
		});
		if (!sortKey) return filtered;
		const direction = sortDirection === 'asc' ? 1 : -1;
		return [...filtered].sort((a, b) => a.fullName.localeCompare(b.fullName, 'tr') * direction);
	}, [students, groupFilter, gradeFilter, sortKey, sortDirection]);

	const handleSort = useCallback(
		(key: StudentSortKeyValue) => {
			if (key === sortKey) {
				setSortDirection(prev => (prev === 'asc' ? 'desc' : 'asc'));
			} else {
				setSortKey(key);
				setSortDirection(DEFAULT_SORT_DIRECTION);
			}
		},
		[sortKey]
	);

	const handleToggleSelect = useCallback((studentId: number) => {
		setSelectedIds(prev => (prev.includes(studentId) ? prev.filter(id => id !== studentId) : [...prev, studentId]));
	}, []);

	const handleToggleSelectAll = useCallback(() => {
		const visibleIds = visibleStudents.map(student => student.id);
		const allSelected = visibleIds.every(id => selectedIds.includes(id));
		setSelectedIds(prev => (allSelected ? prev.filter(id => !visibleIds.includes(id)) : Array.from(new Set([...prev, ...visibleIds]))));
	}, [selectedIds, visibleStudents]);

	const [pendingGroupChange, setPendingGroupChange] = useState<PendingGroupChange | null>(null);
	const handleGroupChange = useCallback(
		(studentId: number, groupId: number | null) => {
			const student = knownStudents[studentId];
			if (!student || student.groupId === groupId) return;
			setPendingGroupChange({
				studentId,
				fullName: student.fullName,
				fromGroupName: student.groupName,
				toGroupId: groupId,
				toGroupName: groups.find(group => group.id === groupId)?.name ?? null
			});
		},
		[groups, knownStudents]
	);
	const handleCancelGroupChange = useCallback(() => setPendingGroupChange(null), []);
	const handleConfirmGroupChange = useCallback(() => {
		if (!pendingGroupChange) return;
		const { studentId, toGroupId } = pendingGroupChange;
		if (toGroupId === null) {
			void removeStudentFromGroup(studentId);
		} else {
			void moveStudentsToGroup(toGroupId, [studentId]);
		}
		setPendingGroupChange(null);
	}, [moveStudentsToGroup, pendingGroupChange, removeStudentFromGroup]);
	const isGroupRemoval = pendingGroupChange?.toGroupId === null;

	const handleRenameGroup = useCallback((groupId: number, name: string) => void renameGroup(groupId, name), [renameGroup]);

	const drawerAvailableStudents = useMemo<BoutiqueStudent[]>(() => {
		if (!drawerGroup) return [];
		const memberIds = new Set(drawerGroup.students.map(member => member.id));
		return pickerStudents.filter(student => !memberIds.has(student.id)).sort((a, b) => a.fullName.localeCompare(b.fullName, 'tr'));
	}, [drawerGroup, pickerStudents]);

	const handleAddStudentToGroup = useCallback((groupId: number, studentId: number) => void moveStudentsToGroup(groupId, [studentId]), [moveStudentsToGroup]);
	const [pendingRemoval, setPendingRemoval] = useState<PendingMemberRemoval | null>(null);
	const handleRemoveStudentFromGroup = useCallback(
		(studentId: number) => {
			const student = drawerStudents.find(item => item.id === studentId) ?? knownStudents[studentId];
			if (student) setPendingRemoval({ kind: RemoveMemberKind.STUDENT, id: student.id, fullName: student.fullName });
		},
		[drawerStudents, knownStudents]
	);
	const handleRemoveExpertFromGroup = useCallback(
		(groupMemberId: number) => {
			const expert = drawerGroup?.experts.find(item => item.groupMemberId === groupMemberId);
			if (expert) setPendingRemoval({ kind: RemoveMemberKind.EXPERT, id: expert.groupMemberId, fullName: expert.fullName });
		},
		[drawerGroup]
	);
	const handleCancelRemoval = useCallback(() => setPendingRemoval(null), []);
	const handleConfirmRemoval = useCallback(() => {
		if (!pendingRemoval) return;
		if (pendingRemoval.kind === RemoveMemberKind.STUDENT) {
			void removeStudentFromGroup(pendingRemoval.id);
		} else {
			void removeGroupMember(pendingRemoval.id);
		}
		setPendingRemoval(null);
	}, [pendingRemoval, removeGroupMember, removeStudentFromGroup]);

	const selectedStudents = useMemo(() => selectedIds.map(id => knownStudents[id]).filter((student): student is BoutiqueStudent => Boolean(student)), [knownStudents, selectedIds]);
	const selectedPickerItems = useMemo(() => selectedStudents.map(student => toStudentPickerItem(student, groups)), [groups, selectedStudents]);

	const handleSubmitCreateGroup = useCallback(
		async (values: CreateGroupFormValues) => {
			const gradeId = resolveGroupGradeId(selectedStudents.filter(student => values.studentIds.includes(student.id)));
			const result = await createGroupFromStudents({ name: values.name, gradeId, studentIds: values.studentIds });
			if (!result) return;
			setIsGroupModalOpen(false);
			setSelectedIds([]);
		},
		[createGroupFromStudents, selectedStudents]
	);

	const [isPlannedLessonOpen, setIsPlannedLessonOpen] = useState(false);
	const [plannedLessonBlockers, setPlannedLessonBlockers] = useState<PlannedLessonBlocker[]>([]);

	const handleOpenPlannedLesson = useCallback(() => {
		const blockers = selectedStudents.flatMap(student => {
			const reason = getPlannedLessonBlocker(student, groups);
			return reason ? [{ id: student.id, name: student.fullName, reason }] : [];
		});
		if (blockers.length) {
			setPlannedLessonBlockers(blockers);
			return;
		}
		setIsPlannedLessonOpen(true);
	}, [groups, selectedStudents]);

	const handleRemovePlannedLessonStudent = useCallback((studentId: number) => setSelectedIds(prev => prev.filter(id => id !== studentId)), []);

	const handleFinishPlannedLesson = useCallback(() => {
		setIsPlannedLessonOpen(false);
		setSelectedIds([]);
	}, []);

	const isLoading = isStudentsLoading || isGroupsLoading;
	const isBusy = !isLoading && (isStudentsFetching || isMutating);

	return (
		<div className="boutique-students">
			<div className="boutique-students__heading">
				<h1 className="boutique-students__title">Öğrencilerim</h1>
				<p className="boutique-students__description">Tüm öğrencilerinizi görüntüleyin, gruplarını yönetin ve gelişimlerini değerlendirin.</p>
			</div>

			<div className="boutique-students__groups">
				<AllGroupsCard
					groupCount={groups.length}
					isActive={selectedGroupCard === ALL_GROUPS_ID}
					onSelect={() => handleGroupCardSelect(ALL_GROUPS_ID)}
				/>
				{groups.map(group => (
					<GroupCard
						key={group.id}
						group={group}
						isActive={selectedGroupCard === group.id}
						onSelect={handleGroupCardSelect}
					/>
				))}
			</div>

			<div className="boutique-students__filters">
				<Dropdown<FilterOption<number>>
					className="boutique-students__filter"
					options={groupFilterOptions}
					selectedOption={groupFilter}
					onOptionSelect={handleGroupFilterSelect}
					size="small"
				/>
				<Dropdown<FilterOption<string>>
					className="boutique-students__filter"
					options={gradeOptions}
					selectedOption={gradeFilter}
					onOptionSelect={handleGradeFilterSelect}
					placeholder={GRADE_FILTER_PLACEHOLDER}
					size="small"
				/>
			</div>

			{isLoading ? (
				<div className="boutique-students__loading">
					<div
						className="boutique-students__spinner"
						role="status"
						aria-live="polite">
						<span className="boutique-students__spinner-ring" />
						<span className="boutique-students__spinner-text">{LOADING_TEXT}</span>
					</div>
				</div>
			) : (
				<div
					ref={tableRef}
					className="boutique-students__table-wrap">
					<div className="boutique-students__table-area">
						{isBusy && (
							<div
								className="boutique-students__overlay"
								aria-busy="true">
								<div
									className="boutique-students__spinner"
									role="status"
									aria-live="polite">
									<span className="boutique-students__spinner-ring" />
									<span className="boutique-students__spinner-text">{LOADING_TEXT}</span>
								</div>
							</div>
						)}
						<StudentsTable
							students={visibleStudents}
							groupOptions={groupOptions}
							selectedIds={selectedIds}
							sortKey={sortKey}
							sortDirection={sortDirection}
							onToggleSelect={handleToggleSelect}
							onToggleSelectAll={handleToggleSelectAll}
							onSort={handleSort}
							onGroupChange={handleGroupChange}
							onGroupSelected={() => setIsGroupModalOpen(true)}
							onPlanLesson={handleOpenPlannedLesson}
						/>
					</div>
					<div className="boutique-students__pagination">
						<div className="boutique-students__page-size">
							<span className="boutique-students__page-size-label">{PAGE_SIZE_LABEL}</span>
							<Dropdown<FilterOption<number>>
								className="boutique-students__page-size-select"
								options={PAGE_SIZE_OPTIONS}
								selectedOption={pageSizeOption}
								onOptionSelect={handlePageSizeChange}
								size="small"
								menuPosition="top"
							/>
							<span className="boutique-students__total">{totalElements} öğrenci</span>
						</div>
						<Pagination
							currentPage={currentPage}
							totalPages={Math.max(totalPages, PAGINATION_FIRST_PAGE)}
							onPageChange={handlePageChange}
						/>
					</div>
				</div>
			)}

			<DialogModal
				isOpen={pendingRemoval !== null}
				onClose={handleCancelRemoval}
				variant="warning"
				className="boutique-dialog"
				title={pendingRemoval ? REMOVE_MEMBER_DIALOG.TITLE[pendingRemoval.kind] : ''}
				primaryButtonText={REMOVE_MEMBER_DIALOG.CONFIRM_LABEL}
				secondaryButtonText={REMOVE_MEMBER_DIALOG.CANCEL_LABEL}
				onPrimaryClick={handleConfirmRemoval}
				onSecondaryClick={handleCancelRemoval}
				showCloseIcon={false}>
				{pendingRemoval && (
					<p>
						<strong>{pendingRemoval.fullName}</strong> {REMOVE_MEMBER_DIALOG.DESCRIPTION_PREFIX[pendingRemoval.kind]} <strong>{drawerGroup?.name}</strong>{' '}
						{REMOVE_MEMBER_DIALOG.DESCRIPTION_SUFFIX[pendingRemoval.kind]}
					</p>
				)}
			</DialogModal>
			<DialogModal
				isOpen={pendingGroupChange !== null}
				onClose={handleCancelGroupChange}
				variant="warning"
				className="boutique-dialog"
				title={isGroupRemoval ? GROUP_CHANGE_DIALOG.REMOVE_TITLE : GROUP_CHANGE_DIALOG.MOVE_TITLE}
				primaryButtonText={isGroupRemoval ? GROUP_CHANGE_DIALOG.REMOVE_CONFIRM_LABEL : GROUP_CHANGE_DIALOG.MOVE_CONFIRM_LABEL}
				secondaryButtonText={GROUP_CHANGE_DIALOG.CANCEL_LABEL}
				onPrimaryClick={handleConfirmGroupChange}
				onSecondaryClick={handleCancelGroupChange}
				showCloseIcon={false}>
				{pendingGroupChange && (
					<p>
						<strong>{pendingGroupChange.fullName}</strong> {GROUP_CHANGE_DIALOG.STUDENT_PREFIX}{' '}
						{pendingGroupChange.fromGroupName && (
							<>
								<strong>{pendingGroupChange.fromGroupName}</strong> {GROUP_CHANGE_DIALOG.FROM_SUFFIX}{' '}
							</>
						)}
						{isGroupRemoval ? (
							GROUP_CHANGE_DIALOG.REMOVE_SUFFIX
						) : (
							<>
								<strong>{pendingGroupChange.toGroupName}</strong> {pendingGroupChange.fromGroupName ? GROUP_CHANGE_DIALOG.MOVE_SUFFIX : GROUP_CHANGE_DIALOG.ADD_SUFFIX}
							</>
						)}{' '}
						{GROUP_CHANGE_DIALOG.QUESTION}
					</p>
				)}
			</DialogModal>
			<CreateGroupModal
				isOpen={isGroupModalOpen}
				onClose={() => setIsGroupModalOpen(false)}
				onSubmit={handleSubmitCreateGroup}
				students={selectedPickerItems}
				isSubmitting={isMutating}
			/>

			<PlannedLessonModal
				isOpen={isPlannedLessonOpen}
				students={selectedStudents}
				groups={groups}
				onRemoveStudent={handleRemovePlannedLessonStudent}
				onClose={() => setIsPlannedLessonOpen(false)}
				onFinish={handleFinishPlannedLesson}
			/>
			<DialogModal
				isOpen={plannedLessonBlockers.length > 0}
				onClose={() => setPlannedLessonBlockers([])}
				variant="warning"
				className="boutique-dialog"
				title={PLANNED_LESSON_BLOCKER.TITLE}
				primaryButtonText={PLANNED_LESSON_DIALOG_CONFIRM}
				onPrimaryClick={() => setPlannedLessonBlockers([])}
				showCloseIcon={false}>
				<p>{PLANNED_LESSON_BLOCKER.DESCRIPTION}</p>
				<ul className="planned-blockers">
					{plannedLessonBlockers.map(blocker => (
						<li
							key={blocker.id}
							className="planned-blockers__item">
							<span className="planned-blockers__name">{blocker.name}</span>
							<span className="planned-blockers__reason">
								<i className="icons-danger-circle" />
								{blocker.reason}
							</span>
						</li>
					))}
				</ul>
				<p className="planned-blockers__footnote">{PLANNED_LESSON_BLOCKER.FOOTNOTE}</p>
			</DialogModal>

			<GroupDetailDrawer
				group={drawerGroup}
				students={drawerStudents}
				availableStudents={drawerAvailableStudents}
				isMutating={isMutating}
				onClose={handleCloseDrawer}
				onRename={handleRenameGroup}
				onRemoveExpert={handleRemoveExpertFromGroup}
				onAddStudent={handleAddStudentToGroup}
				onRemoveStudent={handleRemoveStudentFromGroup}
			/>
		</div>
	);
};

export default BoutiqueStudentsScreen;
